/**
 * Server PDF API (/api/flyers/event/pdf, /api/flyers/generate) expects Fabric-style
 * top-level text fields (fontFamily, underline, etc.). Konva stores them under `typography`.
 * These helpers flatten typography for API persistence without breaking editor reload.
 */

import { constraintsToFieldConfig } from './fieldConfigMigration';

export function buildFabricFontString(typography = {}) {
  const weight =
    typography.fontWeight === 'bold' || typography.fontWeight === '700' ? 'bold' : 'normal';
  const style = typography.fontStyle === 'italic' ? 'italic' : 'normal';
  const size = Number(typography.fontSize) || 12;
  const family = String(typography.fontFamily || 'Arial').replace(/"/g, '');
  return `${style} ${weight} ${size}px "${family}"`;
}

/** Flatten Konva text node for server PDF generators (Fabric-compatible fields). */
export function enrichTextNodeForServerPdf(node) {
  if (node?.type !== 'text') return node;

  const ty = node.typography || {};
  const t = node.transform || {};
  const constraints = node.constraints || {};
  const fs = node.layout?.fontSize || ty.fontSize || 12;

  const typography = {
    ...ty,
    fontFamily: ty.fontFamily || node.fontFamily || 'Arial',
    fontSize: fs,
    fontWeight: ty.fontWeight || node.fontWeight || 'normal',
    fontStyle: ty.fontStyle || node.fontStyle || 'normal',
    underline: Boolean(ty.underline ?? node.underline),
    fill: ty.fill || node.fill || '#111827',
    textAlign: ty.textAlign || node.textAlign || 'left',
    lineHeight: ty.lineHeight > 0 ? ty.lineHeight : ty.lineHeight ?? 1.16,
    letterSpacing: ty.letterSpacing ?? node.charSpacing ?? 0,
  };

  const fieldConfig = constraintsToFieldConfig(t, typography, constraints, {
    fieldKey: node.fieldKey,
    ...(node.fieldConfig && typeof node.fieldConfig === 'object' ? node.fieldConfig : {}),
  });

  return {
    ...node,
    typography,
    fieldConfig,
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle,
    underline: typography.underline,
    fill: typography.fill,
    textAlign: typography.textAlign,
    lineHeight: typography.lineHeight,
    charSpacing: typography.letterSpacing,
    font: buildFabricFontString(typography),
    left: t.x ?? node.left,
    top: t.y ?? node.top,
    width: t.width ?? node.width,
    height: t.height ?? node.height,
    angle: t.rotation ?? node.angle ?? 0,
    type: node.fabricType || node.type || 'textbox',
  };
}

function konvaImageToFabricObject(node) {
  const t = node.transform || {};
  const key = String(node.objectKey || node.gcpObjectKey || '').trim();
  return {
    type: 'image',
    id: node.id,
    fieldKey: node.fieldKey,
    src: node.src,
    objectKey: key || undefined,
    gcpObjectKey: key || undefined,
    left: t.x,
    top: t.y,
    width: t.width,
    height: t.height,
    angle: t.rotation || 0,
    fieldConfig: node.fieldConfig,
  };
}

function konvaShapeToFabricObject(node) {
  const t = node.transform || {};
  return {
    type: node.fabricType || 'rect',
    id: node.id,
    fill: node.fill,
    stroke: node.stroke,
    strokeWidth: node.strokeWidth,
    left: t.x,
    top: t.y,
    width: t.width,
    height: t.height,
    angle: t.rotation || 0,
    radius: node.radius,
    points: node.points,
  };
}

/** Full scene → Fabric `objects[]` array for legacy server PDF renderers. */
export function sceneGraphToFabricObjects(scene) {
  const sorted = [...(scene?.objects || [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  return sorted.map((node) => {
    if (node.type === 'text') return konvaTextToFabricExportObject(node);
    if (node.type === 'image') return konvaImageToFabricObject(node);
    if (node.type === 'shape') return konvaShapeToFabricObject(node);
    return node;
  });
}

export function konvaTextToFabricExportObject(node) {
  const enriched = enrichTextNodeForServerPdf(node);
  const t = enriched.transform || {};
  return {
    type: enriched.fabricType || 'textbox',
    id: enriched.id,
    fieldKey: enriched.fieldKey,
    fieldConfig: enriched.fieldConfig,
    text: String(enriched.text ?? ''),
    left: t.x,
    top: t.y,
    width: t.width,
    height: t.height,
    angle: t.rotation || 0,
    fontFamily: enriched.fontFamily,
    fontSize: enriched.fontSize,
    fontWeight: enriched.fontWeight,
    fontStyle: enriched.fontStyle,
    underline: enriched.underline,
    fill: enriched.fill,
    textAlign: enriched.textAlign,
    lineHeight: enriched.lineHeight,
    charSpacing: enriched.charSpacing,
    font: enriched.font,
    layout: enriched.layout,
  };
}

export function enrichSceneForServerPdf(scene) {
  if (!scene) return scene;
  const objects = (scene.objects || []).map((o) =>
    o.type === 'text' ? enrichTextNodeForServerPdf(o) : o,
  );
  return { ...scene, objects };
}

export function collectFontsUsedFromScene(scene) {
  const fonts = new Set();
  for (const o of scene?.objects || []) {
    if (o.type !== 'text') continue;
    const f = o.typography?.fontFamily || o.fontFamily;
    if (f) fonts.add(f);
  }
  return [...fonts];
}

/** True when saved JSON lacks Fabric top-level font fields the server PDF API expects. */
export function templateJsonNeedsServerFontMigration(json) {
  const doc = json && typeof json === 'object' ? json : null;
  if (!doc) return false;

  const checkObjects = (objects) =>
    (objects || []).some(
      (o) =>
        o?.type === 'text' &&
        (o.typography?.fontFamily || o.typography?.underline != null) &&
        (o.fontFamily == null || o.underline === undefined),
    );

  if (Array.isArray(doc.pages)) {
    return doc.pages.some((p) => checkObjects(p?.scene?.objects));
  }
  if (doc.scene?.objects) return checkObjects(doc.scene.objects);
  if (Array.isArray(doc.objects)) return checkObjects(doc.objects);
  return false;
}
